import * as THREE from 'three';
import type { Brand } from '../brand/types';
import { outcomeFromRun } from '../sim/autoplay';
import { newDrive, suggestPlayType } from '../sim/drive';
import { ballOnToZ } from '../sim/field';
import { base43 } from '../sim/formations';
import {
  fieldGoalChance,
  fieldGoalDistance,
  fieldGoalRange,
  newMatch,
  puntResult,
  simulateOpponentDrive,
  tickClock,
  type MatchState,
} from '../sim/match';
import {
  breakOnBall,
  finishPlay,
  handoffPoint,
  headline,
  planThrow,
  previewCatch,
  qbPosition,
  resolveCatch,
  snap,
  startCatchRun,
  startRun,
  whyLines,
  type LivePlay,
  type PlayOutcome,
  type ThrowPlan,
} from '../sim/playEngine';
import { gradeCatchTap } from '../sim/passing';
import { sepLabel, type SepLabel, type Vec2 } from '../sim/playSim';
import { PLAYS } from '../sim/plays';
import { NO_INPUT, type RacEvent, type RacWorld } from '../sim/rac';
import { Rng, seedFrom } from '../sim/rng';
import { generateSquad } from '../sim/roster';
import teams from '../sim/teams.json';
import type { Tunables } from '../sim/tunables';
import type { CatchTiming, DriveState, Play, Player, Position } from '../sim/types';
import { Hud } from '../ui/hud';
import { BigScreen, type ClipLoader } from '../ui/bigScreen';
import { Guide } from '../ui/guide';
import { CatchRing, Pops, RunControls, YardCounter } from '../ui/liveControls';
import { PlayCallView } from '../ui/playCall';
import { ReceiverIcons } from '../ui/receiverIcons';
import { Banner, FinalCard, FourthDown, InfoCard, TitleScreen } from '../ui/screens';
import { type Action, type AthleteRig, type KitLogos, type SquadSpec, AthletesView } from './athletes';
import type { AthleteModel } from './athleteModel';
import { SkinnedAthletesView } from './skinnedAthletes';
import { GameAudio, haptic } from './audio';
import { CameraRig } from './camera';
import { ArcPreview, BallTrail, BallView, Confetti, Puff, TargetRing } from './fx';
import { createStage, type SceneStats } from './scene';

export type Phase =
  | 'title'
  | 'call'
  | 'fourth'
  | 'live'
  | 'handoff'
  | 'air'
  | 'run'
  | 'intReturn'
  | 'dead'
  | 'result'
  | 'info'
  | 'final';

export interface GameDebug {
  phase: Phase;
  drive: DriveState;
  match: MatchState;
  lastHeadline: string;
}

export interface Game {
  stats: SceneStats;
  debug: GameDebug;
  /** Which players are on the field: the rigged model, or the primitive fallback. */
  athletes: 'rigged' | 'primitive';
  dispose(): void;
}

export interface GameOptions {
  container: HTMLElement;
  uiRoot: HTMLElement;
  brand: Brand;
  logo: HTMLImageElement | null;
  /** The title screen's hero picture (brand.json title.hero), or null to show the stadium behind it. */
  titleHero: HTMLImageElement | null;
  /** The animated logo for the title screen, as markup (see loadBrandSvg), or null for the name as text. */
  titleLogo: SVGSVGElement | null;
  /** The brand's touchdown clips for the big screen (brand.json celebrations): how many, and how to load one. */
  celebrationClips: { count: number; load: ClipLoader };
  /** The brand's small mark (helmets and shoulder pads). */
  mark: HTMLImageElement | null;
  tunables: Tunables;
  matchSeed: string;
  /** Debug time scale (?speed=4). */
  timeScale: number;
  /** Skip the title (tests). */
  skipTitle: boolean;
  /** Start the guided first drive again (?guide). */
  resetGuide: boolean;
  /** The rigged athlete; null falls back to the primitive players. */
  athleteModel: AthleteModel | null;
}

const OFF_FACE = Math.PI;
const DEF_FACE = 0;
const SIDELINE = 12;

/** Quick Match: four one-minute quarters, you have the ball, every play is live. */
export function createGame(o: GameOptions): Game {
  const t = o.tunables;
  const stage = createStage(o.container, o.brand, o.logo, t);
  const rig = new CameraRig(stage.camera, t);
  const audio = new GameAudio(t);

  const opponent = teams.opponents[0]!;
  const squadRng = new Rng(seedFrom(`${o.matchSeed}:squads`));
  const offense: Player[] = generateSquad(squadRng, o.brand.id, PLAYS[0]!.formation, t.ratings.home, t);
  const defense: Player[] = generateSquad(squadRng, 'opponent', base43, t.ratings.away, t);
  const benchNumbers = (n: number) => Array.from({ length: n }, () => squadRng.int(1, 99));
  const squads: SquadSpec[] = [
    { uniform: o.brand.uniforms.home, numbers: offense.map((p) => p.number), branded: true },
    { uniform: teams.opponentUniform, numbers: defense.map((p) => p.number) },
    { uniform: o.brand.uniforms.home, numbers: benchNumbers(SIDELINE), branded: true },
    { uniform: teams.opponentUniform, numbers: benchNumbers(SIDELINE) },
  ];
  const athletes = createAthletes(squads, t, o.athleteModel, { word: o.logo, mark: o.mark });
  stage.scene.add(athletes.group);
  const ball = new BallView(t);
  const arc = new ArcPreview(o.brand, t);
  const ring = new TargetRing(o.brand);
  const carrierRing = new TargetRing(o.brand);
  const trail = new BallTrail(o.brand, t.fx.trailPoints, t.fx.trailWidthYards);
  const confetti = new Confetti(o.brand, t);
  const puff = new Puff(t);
  stage.scene.add(ball.mesh, ball.shadow, arc.object, ring.mesh, carrierRing.mesh, trail.mesh, confetti.mesh, puff.points);

  // UI.
  const hud = new Hud(o.brand.teamName, opponent.name);
  const playCall = new PlayCallView({ onPick: pick, onSnap: doSnap, onFlip: flipFormation });
  const icons = new ReceiverIcons(t, { onHoldStart, onRelease });
  const catchRing = new CatchRing();
  const runCtl = new RunControls(t);
  const pops = new Pops();
  const yards = new YardCounter();
  const guide = new Guide(t, o.resetGuide);
  const banner = new Banner(() => nextAfterResult(true), t.ui.holdToReadSeconds);
  const fourth = new FourthDown(onFourth, t.ui.buttonGuardSeconds);
  const info = new InfoCard(skipInfo);
  const final = new FinalCard(() => startMatch(), t.ui.buttonGuardSeconds);
  const bigScreen = new BigScreen('TOUCHDOWN');
  // A different clip each touchdown, starting from a random one so a returning player does not always see the same first.
  bigScreen.setClips(o.celebrationClips.count, o.celebrationClips.load, Math.floor(Math.random() * o.celebrationClips.count));
  const titleContent = {
    logo: o.titleLogo,
    brandName: o.brand.name,
    lines: o.brand.title?.lines ?? [{ text: o.brand.tagline }],
    sub: `Quick match · ${o.brand.teamName} vs ${opponent.name}`,
    hero: o.titleHero,
  };
  const title = new TitleScreen(titleContent, () => {
    audio.unlock();
    audio.ui();
    title.hide();
    startMatch();
  });
  o.uiRoot.append(
    hud.root,
    runCtl.root,
    icons.root,
    catchRing.layerNode,
    catchRing.root,
    yards.root,
    pops.root,
    guide.root,
    bigScreen.root,
    playCall.root,
    banner.root,
    fourth.root,
    info.root,
    final.root,
    title.root,
  );

  const n = offense.length;
  const offPos = new Float32Array(n * 2);
  const defPos = new Float32Array(defense.length * 2);
  const defAtThrow = new Float32Array(defense.length * 2);
  const scratchDef = new Float32Array(defense.length * 2);
  const v3 = new THREE.Vector3();
  const v3b = new THREE.Vector3();
  const anchors: { x: number; y: number }[] = [];
  const states: SepLabel[] = [];
  const matchRng = new Rng(seedFrom(`${o.matchSeed}:match`));

  let match: MatchState = newMatch(t);
  let drive: DriveState = newDrive(t);
  let driveNo = 0;
  let flip = false;
  let phase: Phase = 'title';
  let play: Play = PLAYS.find((p) => p.id === 'slants')!;
  let picked = false;
  let fourthDecided = false;
  let playClock = 0;
  let autoSnap: number | null = null;
  let live: LivePlay | null = null;
  let plan: ThrowPlan | null = null;
  let world: RacWorld | null = null;
  /** Who holds the ball this frame (set by holdBallAt), so it can follow the hand after the pose is drawn. */
  let held: { i: number; side: 0 | 1 } | null = null;
  let runKind: 'pass' | 'run' = 'pass';
  let pass: PlayOutcome['pass'] = null;
  let timing: CatchTiming | null = null;
  let tapped: CatchTiming | null = null;
  let outcome: PlayOutcome | null = null;
  let carrierIdx = -1;
  let time = 0;
  let phaseT = 0;
  let iconTick = 0;
  let slowT = 0;
  let pressured = false;
  let ringOpen = false;
  let infoNext: (() => void) | null = null;
  let infoHold = 0;
  let resultLeft = 0;
  let nextQueued = false;
  let hitStop = 0;
  let atBall = false;
  let firstDownShown = false;
  /** Real seconds left in the guide's pause of the first pass (it gives up after a while). */
  let guidePause = 0;
  let guideHeld = false;
  let guideSlow = false;
  let playsRun = 0;
  const debug: GameDebug = { phase, drive, match, lastHeadline: '' };

  const offIdx = (i: number) => athletes.index(0, i);
  const defIdx = (j: number) => athletes.index(1, j);

  function setPhase(p: Phase): void {
    phase = p;
    phaseT = 0;
    debug.phase = p;
  }

  function ballZ(): number {
    return ballOnToZ(drive.ballOn, t);
  }

  function wz(y: number): number {
    return ballZ() - y;
  }

  function stanceFor(pos: Position): Action {
    if (pos === 'OL' || pos === 'DL') return 'lineStance';
    if (pos === 'QB') return 'qbReady';
    return 'stance';
  }

  function project(world3: THREE.Vector3, out: { x: number; y: number }): { x: number; y: number } {
    v3b.copy(world3).project(stage.camera);
    out.x = ((v3b.x + 1) / 2) * stage.size.width;
    out.y = ((1 - v3b.y) / 2) * stage.size.height;
    return out;
  }

  const scr = { x: 0, y: 0 };
  function popAt(i: number, text: string, kind: 'good' | 'bad' | 'plain' = 'good', big = false): void {
    athletes.headPosition(i, v3);
    v3.y += 0.6;
    project(v3, scr);
    pops.pop(text, scr.x, scr.y, kind, big);
  }

  function placeSidelines(): void {
    const f = t.field;
    const x = f.widthYards / 2 + t.stadium.benchOffsetYards;
    for (let k = 0; k < SIDELINE; k++) {
      const z = ballZ() - 12 + k * 2.2;
      athletes.teleport(athletes.index(2, k), x + (k % 2) * 0.9, z, 'idle', -Math.PI / 2);
      athletes.teleport(athletes.index(3, k), -x - (k % 2) * 0.9, z, 'idle', Math.PI / 2);
    }
  }

  function showFormation(): void {
    const m = flip ? -1 : 1;
    play.formation.slots.forEach((s, i) => athletes.teleport(offIdx(i), s.x * m, wz(s.y), stanceFor(s.pos), OFF_FACE));
    base43.slots.forEach((s, j) => athletes.teleport(defIdx(j), s.x * m, wz(s.y), stanceFor(s.pos), DEF_FACE));
    placeSidelines();
    const qbSlot = play.formation.slots.findIndex((s) => s.pos === 'QB');
    athletes.update(0);
    ball.show(true);
    holdBallAt(offIdx(qbSlot), 1);
  }

  function resetFx(): void {
    arc.hide();
    ring.hide();
    carrierRing.hide();
    trail.reset();
    catchRing.close();
    yards.show(false);
    icons.show(false);
    runCtl.show(false);
    ringOpen = false;
  }

  /** A hit: a short freeze, a camera jolt, a puff of turf, the sound and a buzz. */
  function impact(x: number, z: number, kind: 'tackle' | 'broke' | 'sack'): void {
    const f = t.fx;
    hitStop = kind === 'broke' ? f.hitStopBrokeSeconds : f.hitStopSeconds;
    rig.addShake(kind === 'tackle' ? f.shakeTackle : kind === 'sack' ? f.shakeSack : f.shakeBroke, kind !== 'broke');
    puff.burst(x, z);
    audio.hit(kind !== 'broke');
    haptic(f.hapticHitMs);
  }

  // Match flow.

  function startMatch(): void {
    final.hide();
    match = newMatch(t);
    debug.match = match;
    driveNo = 0;
    playsRun = 0;
    newPossession(t.match.kickoffBallOn);
  }

  function newPossession(ballOn: number): void {
    drive = { ...newDrive(t), ballOn };
    driveNo++;
    fourthDecided = false;
    beginPlayCall();
  }

  function beginPlayCall(): void {
    if (match.over) return showFinal();
    live = null;
    plan = null;
    world = null;
    outcome = null;
    pass = null;
    timing = null;
    tapped = null;
    picked = false;
    autoSnap = null;
    pressured = false;
    carrierIdx = -1;
    atBall = false;
    firstDownShown = false;
    guidePause = 0;
    guideHeld = false;
    guideSlow = false;
    resetFx();
    banner.hide();
    guide.hide();
    lineUp();
    audio.crowd(0.1);
    if (drive.down === 4 && !fourthDecided) {
      const dist = fieldGoalDistance(drive.ballOn, t);
      const k = offense.find((p) => p.pos === 'QB')!.ratings.KPW;
      const inRange = dist <= fieldGoalRange(Math.max(k, 80), t);
      fourth.show(`${Math.round(drive.toGo)} to go at the ${drive.ballOn < 50 ? 'own' : 'opp'} ${drive.ballOn < 50 ? Math.round(drive.ballOn) : Math.round(100 - drive.ballOn)}`, inRange ? `FIELD GOAL ${dist} YDS` : null);
      setPhase('fourth');
      return;
    }
    playClock = t.playCall.playClockSeconds;
    playCall.show(suggestPlayType(drive, t));
    playCall.setClock(playClock);
    playCall.setAutoSnap(null);
    if (guide.need('call')) guide.say('PICK A PLAY', 'The outlined card is the coach\'s pick. Then tap SNAP.', 'top');
    setPhase('call');
  }

  /** The field at the current drive's spot: markers, both teams lined up, the camera behind the QB. */
  function lineUp(): void {
    debug.drive = drive;
    hud.update(drive, match);
    stage.field.setMarkers(drive.ballOn, drive.toGo);
    stage.stadium.setExcitement(0.15);
    showFormation();
    const qbSlot = play.formation.slots.findIndex((s) => s.pos === 'QB');
    rig.setMode('call');
    rig.setPullback(0);
    rig.snapTo(0, wz(play.formation.slots[qbSlot]!.y));
  }

  function onFourth(choice: 'go' | 'punt' | 'fg'): void {
    if (phase !== 'fourth') return;
    fourth.hide();
    audio.ui();
    fourthDecided = true;
    if (choice === 'go') return beginPlayCall();
    if (choice === 'punt') {
      const start = puntResult(drive.ballOn, 80, matchRng, t);
      return showInfo('PUNT', `${o.brand.teamName} punt. ${opponent.name} start at their ${start}.`, () => opponentDrive(start));
    }
    const dist = fieldGoalDistance(drive.ballOn, t);
    const chance = fieldGoalChance(dist, 0.1, t);
    const { roll, hit } = matchRng.rollDetailed(chance);
    if (hit) {
      match.home += 3;
      audio.fieldGoal();
      hud.flashScore();
      stage.stadium.setExcitement(1);
    } else audio.incomplete();
    hud.update(drive, match);
    return showInfo(
      hit ? 'FIELD GOAL IS GOOD' : 'NO GOOD',
      `${dist} yards. Chance ${Math.round(chance)}%, roll ${Math.round(roll)}.`,
      () => opponentDrive(hit ? t.match.kickoffBallOn : Math.max(20, Math.round(100 - drive.ballOn))),
    );
  }

  function showInfo(head: string, body: string, next: () => void): void {
    resetFx();
    playCall.hide();
    info.show(head, body);
    infoNext = next;
    infoHold = t.ui.endCardSeconds;
    setPhase('info');
  }

  /** A tap on an info card moves on. A tap in its first moments is ignored: it was meant for the screen before. */
  function skipInfo(): void {
    if (phase !== 'info' || phaseT < t.ui.cardMinShowSeconds) return;
    infoHold = 0;
  }

  function opponentDrive(startBallOn: number): void {
    if (match.over) return showFinal();
    const rng = new Rng(seedFrom(`${o.matchSeed}:opp:${driveNo}`));
    const d = simulateOpponentDrive(rng, startBallOn, t.match.oppRatingAway, t.match.oppRatingHome, t);
    match.away += d.points;
    tickClock(match, d.seconds, t);
    hud.setScore(match);
    const what = { touchdown: 'TOUCHDOWN', fieldGoal: 'FIELD GOAL', punt: 'PUNT', turnover: 'TURNOVER' }[d.result];
    const body = `${d.plays} plays, ${d.yards} yards. ${match.over ? 'That is the game.' : d.result === 'turnover' ? 'Your ball!' : 'Your ball.'}`;
    if (d.points > 0) {
      hud.flashScore();
      audio.incomplete();
    } else audio.crowd(0.6);
    const nextStart = d.result === 'turnover' ? Math.min(60, Math.max(20, 100 - (startBallOn + d.yards))) : d.result === 'punt' ? rng.int(15, 35) : t.match.kickoffBallOn;
    // The card sits over your next line-up, not over the last play (a scorer still dancing in the end zone).
    if (!match.over) {
      drive = { ...newDrive(t), ballOn: nextStart };
      lineUp();
    }
    showInfo(`${opponent.name.toUpperCase()}: ${what}`, body, () => newPossession(nextStart));
  }

  function showFinal(): void {
    resetFx();
    playCall.hide();
    const won = match.home > match.away;
    const tied = match.home === match.away;
    final.show(won ? 'YOU WIN' : tied ? 'TIED' : 'FINAL', `${o.brand.teamName.toUpperCase()} ${match.home}-${match.away} ${opponent.name.toUpperCase()}`, `${playsRun} plays`);
    if (won) {
      confetti.burst(0, ballZ() - 10);
      audio.win();
    }
    setPhase('final');
  }

  // Play call.

  function pick(p: Play): void {
    if (phase !== 'call') return;
    audio.ui();
    play = p;
    picked = true;
    autoSnap = t.playCall.autoSnapSeconds;
    showFormation();
  }

  function flipFormation(): void {
    if (phase !== 'call') return;
    flip = !flip;
    showFormation();
    if (picked) autoSnap = t.playCall.autoSnapSeconds;
  }

  function doSnap(): void {
    if (phase !== 'call' || !picked) return;
    playCall.hide();
    if (guide.need('call')) {
      guide.done('call');
      guide.hide();
    }
    live = snap({ drive, play, flip, offense, defense, defenseFormation: base43, seed: seedFrom(`${o.matchSeed}:${driveNo}:${drive.plays}`), t });
    audio.snap();
    haptic(t.fx.hapticSnapMs);
    time = 0;
    playsRun++;
    // Sweep up from the hero shot into the reading view.
    rig.setMode('qb');
    if (play.type === 'run') {
      setPhase('handoff');
      return;
    }
    icons.setReceivers(live.sim.receivers.map((r) => r.label));
    icons.show(true);
    iconTick = Infinity;
    setPhase('live');
  }

  // Passing.

  function powerOf(held: number): number {
    return Math.min(100, (held / t.controls.holdFillSeconds) * 100);
  }

  function onHoldStart(index: number): void {
    if (phase !== 'live' || !live) return;
    void index;
    haptic(8);
  }

  function onRelease(index: number, held: number): void {
    if (phase !== 'live' || !live) return;
    if (guidePause > 0 || guide.need('throw')) {
      guidePause = 0;
      guide.done('throw');
      guide.hide();
    }
    const power = held < t.controls.tapMaxSeconds ? undefined : powerOf(held);
    plan = planThrow(live, index, time, power);
    live.sim.positionsAt(time, offPos, defAtThrow);
    athletes.place(offIdx(live.qbSlot), offPos[live.qbSlot * 2]!, wz(offPos[live.qbSlot * 2 + 1]!), 'throw');
    icons.show(false);
    arc.hide();
    audio.whoosh();
    tapped = null;
    ringOpen = false;
    trail.reset();
    rig.setMode('ball');
    rig.setPullback(Math.min(t.cameraFollow.pullbackMaxYards, plan.distance * t.cameraFollow.pullbackPerYard));
    ring.show(plan.to.x, wz(plan.to.y));
    atBall = false;
    setPhase('air');
  }

  /** Grades the catch tap by when it lands against the ball's arrival (a moment late still counts). */
  function onCatchTap(): void {
    if (phase !== 'air' || !plan || !live || tapped) return;
    // A second tap straight after the throw is the throwing thumb, not a catch attempt.
    if (phaseT < t.controls.catchTapGuardSeconds) return;
    tapped = gradeCatchTap(plan.flightSeconds - phaseT, t);
    const before = previewCatch(live, plan, 'good');
    const after = previewCatch(live, plan, tapped);
    const late = phaseT > plan.flightSeconds;
    catchRing.grade(tapped === 'perfect' ? 'PERFECT!' : tapped === 'good' ? (late ? 'JUST IN TIME' : 'GOOD') : 'TOO EARLY', after, after - before);
    audio.catchGrade(tapped);
    if (tapped === 'perfect') haptic(t.fx.hapticCatchMs);
  }

  function arcHeight(p: ThrowPlan): number {
    const c = t.controls;
    const power = p.power ?? p.band.centre;
    const k = Math.min(1, Math.max(0, power / 100));
    return p.distance * (c.lobArcPerYard + (c.bulletArcPerYard - c.lobArcPerYard) * k);
  }

  // Frame helpers.

  function placeFromSim(): void {
    if (!live) return;
    const slots = play.formation.slots;
    const broke = time > live.pocket;
    for (let i = 0; i < n; i++) {
      const pos = slots[i]!.pos;
      const a: Action = pos === 'OL' || (pos === 'TE' && live.sim.receiverIndexOfSlot(i) < 0) ? 'block' : pos === 'QB' ? 'qbReady' : 'run';
      athletes.place(offIdx(i), offPos[i * 2]!, wz(offPos[i * 2 + 1]!), athletes.actionOf(offIdx(i)) === 'throw' ? undefined : a, OFF_FACE);
    }
    base43.slots.forEach((s, j) => {
      const a: Action = s.pos === 'DL' ? (broke ? 'run' : 'block') : 'stance';
      athletes.place(defIdx(j), defPos[j * 2]!, wz(defPos[j * 2 + 1]!), a, DEF_FACE);
    });
  }

  function placeIcons(): void {
    if (!live) return;
    const recv = live.sim.receivers;
    while (anchors.length < recv.length) anchors.push({ x: 0, y: 0 });
    anchors.length = recv.length;
    recv.forEach((r, i) => {
      athletes.headPosition(offIdx(r.slot), v3);
      v3.y += t.ui.iconLiftYards;
      project(v3, anchors[i]!);
    });
    icons.place(anchors, stage.size.width, stage.size.height);
  }

  /** Puts the ball in a player's hand now, and again after this frame's pose (see the end of the frame). */
  function holdBallAt(i: number, side: 0 | 1): void {
    held = { i, side };
    athletes.handPosition(i, side, v3);
    ball.hold(v3, athletes.facing(i));
  }

  function beginRun(w: RacWorld, kind: 'pass' | 'run', idx: number): void {
    world = w;
    runKind = kind;
    carrierIdx = idx;
    resetFx();
    runCtl.show(true);
    const teach = guide.need('run');
    runCtl.showHints(teach);
    if (teach) guide.done('run');
    yards.show(true);
    rig.setMode('chase');
    rig.setPullback(0);
    setPhase('run');
  }

  function onRunEvents(events: RacEvent[]): void {
    if (!world) return;
    for (const e of events) {
      if (e.kind === 'move') {
        if (!e.free) continue;
        audio.juke();
        popAt(carrierIdx, e.move === 'truck' ? 'TRUCK!' : e.move === 'spin' ? 'SPIN!' : 'JUKE!', 'plain');
      } else if (e.kind === 'broke') {
        impact(world.carrier.x, wz(world.carrier.y), 'broke');
        slowT = t.fx.slowmoSeconds;
        popAt(carrierIdx, e.tackle.timing === 'timed' ? 'BROKE IT! TIMED' : 'BROKE IT!', 'good', true);
        stage.stadium.setExcitement(0.8);
        audio.crowd(0.7);
      } else if (e.kind === 'tackled') {
        impact(world.carrier.x, wz(world.carrier.y), 'tackle');
        audio.whistle(t.audio.whistleDelaySeconds);
      } else if (e.kind === 'touchdown') {
        audio.touchdown();
        haptic(t.fx.hapticTdMs);
        rig.addShake(t.fx.shakeTd);
        stage.stadium.setExcitement(1);
      } else if (e.kind === 'outOfBounds' || e.kind === 'safety') {
        audio.whistle(e.kind === 'safety' ? t.audio.whistleDelaySeconds : 0);
      }
    }
  }

  function placeWorld(dt: number): void {
    if (!world || !live) return;
    const intRet = phase === 'intReturn';
    const c = world.carrier;
    athletes.place(carrierIdx, c.x, wz(c.y), world.over ? undefined : 'carry');
    // Blockers are the carrier's side minus the carrier, in slot order.
    const ownSlots = (intRet ? defense : offense).map((_, i) => i);
    const carrierSlot = intRet ? carrierIdx - defIdx(0) : carrierIdx - offIdx(0);
    const blockSlots = ownSlots.filter((i) => i !== carrierSlot && (intRet ? false : runKind === 'run' && (offense[i]!.pos === 'OL' || offense[i]!.pos === 'TE')));
    world.blockers.forEach((b, k) => {
      const slot = blockSlots[k];
      if (slot === undefined) return;
      athletes.place(offIdx(slot), b.x, wz(b.y), b.state === 'engaged' ? 'block' : 'run');
    });
    world.defenders.forEach((d, k) => {
      const idx = intRet ? offIdx(k) : defIdx(k);
      const a: Action = d.state === 'down' ? (athletes.actionOf(idx) === 'dive' || athletes.actionOf(idx) === 'down' ? athletes.actionOf(idx) : 'dive') : d.state === 'engaged' ? 'block' : 'run';
      // Chasers look at the carrier whenever they are not running (reacting, engaged, closing in).
      athletes.place(idx, d.x, wz(d.y), a, Math.atan2(c.x - d.x, wz(c.y) - wz(d.y)));
    });
    // Everyone else jogs to the ball.
    if (!intRet && runKind === 'pass') {
      for (let i = 0; i < n; i++) {
        if (i === carrierSlot) continue;
        const idx = offIdx(i);
        const px = offPos[i * 2]!;
        const py = offPos[i * 2 + 1]!;
        const dx = c.x - px;
        const dy = c.y - py;
        const len = Math.hypot(dx, dy);
        const step = Math.min(len - t.run.supportGapYards, t.run.supportJogYps * dt);
        if (len > t.run.supportGapYards) {
          offPos[i * 2] = px + (dx / len) * step;
          offPos[i * 2 + 1] = py + (dy / len) * step;
        }
        athletes.place(idx, offPos[i * 2]!, wz(offPos[i * 2 + 1]!), 'run');
      }
    }
  }

  function endPlay(o2: PlayOutcome): void {
    outcome = o2;
    runCtl.show(false);
    icons.show(false);
    arc.hide();
    ring.hide();
    carrierRing.hide();
    yards.show(false);
    catchRing.close();
    guide.hide();
    const done = o2.end;
    clipPlayed = false;
    if (done === 'touchdown') {
      clipPlayed = bigScreen.play();
      if (world) athletes.place(carrierIdx, world.carrier.x, wz(world.carrier.y), 'celebrate');
      confetti.burst(world?.carrier.x ?? 0, ballZ() - (world?.carrier.y ?? 0));
    }
    if (done === 'tackled' && carrierIdx >= 0) athletes.place(carrierIdx, world!.carrier.x, wz(world!.carrier.y), 'down');
    // TV shots: look back at a scorer, or a close side-on shot of the pile under the banner.
    if (done !== 'incomplete' && carrierIdx >= 0) {
      athletes.headPosition(carrierIdx, v3);
      const dir = phase === 'intReturn' ? 1 : -1;
      rig.setMode(done === 'touchdown' || done === 'pickSix' ? 'celebrate' : 'tackle', dir);
      // A hard cut, like TV: gliding there would swing the camera over the top of the player.
      rig.snapTo(v3.x, v3.z);
    }
    setPhase('dead');
  }

  /** After a big-screen clip the score has already been celebrated, so play moves straight on (no banner). */
  function showResult(straightOn = false): void {
    if (!outcome || !live) return;
    const h = headline(outcome);
    debug.lastHeadline = h;
    const u = outcome.update;
    let extra = '';
    if (u.event === 'touchdown') {
      match.home += 6;
      const pat = matchRng.roll(t.match.patChance);
      if (pat) match.home += 1;
      extra = pat ? ', EXTRA POINT GOOD' : ', EXTRA POINT MISSED';
      hud.flashScore();
    } else if (u.event === 'safety') {
      match.away += 2;
      hud.flashScore();
    } else if (outcome.end === 'pickSix') {
      match.away += 7;
      hud.flashScore();
    }
    if (straightOn) {
      drive = u.state;
      hud.update(u.ended ? live.ctx.drive : drive, match);
      setPhase('result');
      nextAfterResult(false);
      return;
    }
    const good = u.event === 'touchdown' || u.event === 'firstDown' || (outcome.gain > 0 && outcome.end !== 'incomplete');
    const why = whyLines(live, outcome);
    banner.show(h + extra, why, u.ended ? 'CONTINUE' : 'NEXT PLAY', good ? 'good' : 'bad');
    if (!good) audio.crowd(0.1);
    drive = u.state;
    // A finished drive keeps showing where it ended (not "1st & Goal, OPP 0") until the next possession.
    hud.update(u.ended ? live.ctx.drive : drive, match);
    resultLeft = u.event === 'touchdown' ? t.fx.bannerSeconds * 1.6 : t.fx.bannerSeconds;
    nextQueued = false;
    setPhase('result');
  }

  /** From NEXT PLAY or a tap on the banner. A tap that comes a moment early is kept and acted on. */
  function nextAfterResult(fromTap: boolean): void {
    if (phase !== 'result' || !outcome) return;
    if (fromTap && phaseT < t.ui.resultMinShowSeconds) {
      nextQueued = true;
      return;
    }
    banner.hide();
    audio.ui();
    const u = outcome.update;
    if (!u.ended) {
      fourthDecided = false;
      return beginPlayCall();
    }
    if (match.over) return showFinal();
    if (u.event === 'touchdown' || u.event === 'safety') return opponentDrive(t.match.kickoffBallOn);
    // They scored on the return, so they kick off to you.
    if (outcome.end === 'pickSix') return newPossession(t.match.kickoffBallOn);
    if (u.event === 'interception') return opponentDrive(Math.max(1, Math.min(99, Math.round(100 - intSpot))));
    // Turnover on downs: they take over where the ball is.
    return opponentDrive(Math.max(1, Math.round(100 - drive.ballOn)));
  }

  let intSpot = 0;
  /** A touchdown clip played on the big screen for the play just ended. */
  let clipPlayed = false;

  // The frame.

  function frame(rawDt: number): void {
    // Hit stop: everything but the camera holds still for a moment on impact (presentation only).
    const stopped = hitStop > 0;
    if (stopped) hitStop -= rawDt;
    if (guidePause > 0) guidePause = Math.max(0, guidePause - rawDt);
    const scale = stopped ? 0 : (slowT > 0 ? t.fx.slowmoScale : 1) * (guideSlow ? t.guide.catchSlowScale : 1);
    if (slowT > 0 && !stopped) slowT -= rawDt;
    const dt = rawDt * o.timeScale * scale;
    phaseT += dt;
    held = null;
    const teaching = guidePause > 0 || guideSlow || (phase === 'call' && guide.need('call'));
    const clockRuns = phase === 'call' || phase === 'live' || phase === 'handoff' || phase === 'air' || phase === 'run' || phase === 'intReturn';
    if (clockRuns && !teaching && !match.over) {
      tickClock(match, rawDt * o.timeScale, t);
      hud.setClock(match);
    }

    switch (phase) {
      case 'title':
      case 'final':
      case 'fourth':
        break;
      case 'info': {
        infoHold -= dt;
        if (infoHold <= 0 && infoNext) {
          const next = infoNext;
          infoNext = null;
          info.hide();
          next();
        }
        break;
      }
      case 'call': {
        if (!teaching) playClock -= dt;
        playCall.setClock(playClock);
        if (playClock <= 0 && !picked) playCall.autoPick(suggestPlayType(drive, t));
        if (autoSnap !== null) {
          autoSnap -= dt;
          playCall.setAutoSnap(autoSnap);
          if (autoSnap <= 0) doSnap();
        }
        break;
      }
      case 'live': {
        if (!live) break;
        // First pass of the guided drive: hold the play once the routes are open until a receiver is tapped.
        if (!guideHeld && guide.need('throw') && time >= t.guide.throwPauseSeconds && time < live.pocket && icons.holdingIndex < 0) {
          guideHeld = true;
          guidePause = t.guide.maxPauseSeconds;
          guide.say('TAP A RECEIVER TO THROW', 'Green is open. Hold the icon for a longer throw.', 'top');
        }
        if (guidePause <= 0) time += dt;
        live.sim.positionsAt(time, offPos, defPos);
        placeFromSim();
        holdBallAt(offIdx(live.qbSlot), 1);
        rig.follow(0, wz(offPos[live.qbSlot * 2 + 1]!));
        placeIcons();
        iconTick += dt;
        if (iconTick >= t.ui.iconRefreshSeconds) {
          iconTick = 0;
          states.length = live.sim.receivers.length;
          for (let i = 0; i < states.length; i++) states[i] = sepLabel(live.sim.separation(i, time), t);
          icons.setStates(states);
        }
        const hi = icons.holdingIndex;
        if (hi >= 0) {
          const held = icons.holdSeconds();
          const power = held < t.controls.tapMaxSeconds ? undefined : powerOf(held);
          const p = planThrow(live, hi, time, power);
          icons.setPower(hi, power ?? 0, p.band.lo, p.band.hi);
          athletes.handPosition(offIdx(live.qbSlot), 1, v3);
          v3b.set(p.to.x, t.throw.catchHeightYards, wz(p.to.y));
          arc.set(v3, v3b, power ?? p.band.centre);
        } else arc.hide();
        if (!pressured && time > live.pocket) {
          pressured = true;
          popAt(offIdx(live.qbSlot), 'PRESSURE!', 'bad');
          audio.crowd(0.5);
        }
        if (time >= live.sackTime) {
          const qb = qbPosition(live, time, { x: 0, y: 0 });
          icons.show(false);
          arc.hide();
          guidePause = 0;
          impact(qb.x, wz(qb.y), 'sack');
          audio.whistle(t.audio.whistleDelaySeconds);
          carrierIdx = offIdx(live.qbSlot);
          athletes.place(carrierIdx, qb.x, wz(qb.y), 'down');
          base43.slots.forEach((s, j) => {
            if (s.pos === 'DL') athletes.place(defIdx(j), defPos[j * 2]!, wz(defPos[j * 2 + 1]!), j === 1 ? 'dive' : 'run');
          });
          popAt(carrierIdx, 'SACKED', 'bad', true);
          world = null;
          endPlay(finishPlay(live, { kind: 'pass', end: 'sack', gain: Math.round(qb.y), plan: null, pass: null, timing: null, events: [], carrier: offense[live.qbSlot]! }));
        }
        break;
      }
      case 'handoff': {
        if (!live) break;
        time += dt;
        live.sim.positionsAt(time, offPos, defPos);
        const rbSlot = play.formation.slots.findIndex((s) => s.pos === 'RB');
        const at = play.run?.toss ? t.run.tossSeconds : t.run.handoffSeconds;
        const hp = handoffPoint(live, { x: 0, y: 0 });
        const k = Math.min(1, time / at);
        const start = live.sim.offenseStart[rbSlot]!;
        offPos[rbSlot * 2] = start.x + (hp.x - start.x) * k;
        offPos[rbSlot * 2 + 1] = start.y + (hp.y - start.y) * k;
        placeFromSim();
        rig.follow(0, wz(offPos[live.qbSlot * 2 + 1]!));
        holdBallAt(offIdx(play.run?.toss && k > 0.5 ? rbSlot : live.qbSlot), play.run?.toss && k > 0.5 ? 0 : 1);
        if (k >= 1) {
          const m = flip ? -1 : 1;
          const aim: Vec2 | null = play.run ? { x: play.run.aim.x * m, y: play.run.aim.y } : null;
          const dir = aim ? { x: aim.x - hp.x, y: aim.y - hp.y } : { x: 0, y: 1 };
          const len = Math.hypot(dir.x, dir.y) || 1;
          const w = startRun(live, 'offense', rbSlot, hp, { x: (dir.x / len) * 4, y: (dir.y / len) * 4 }, offPos, defPos, { aim, auto: false, blocking: true });
          beginRun(w, 'run', offIdx(rbSlot));
        }
        break;
      }
      case 'air': {
        if (!live || !plan) break;
        const r = live.sim.receivers[plan.receiverIndex]!;
        const remaining = plan.flightSeconds - phaseT;
        if (remaining <= 0) {
          // The ball is at his hands: everyone holds for a moment in case the catch tap comes a little late.
          if (!atBall) {
            atBall = true;
            time = plan.throwTime + plan.flightSeconds;
            live.sim.positionsAt(time, offPos, defPos);
            breakOnBall(live, plan, time, defPos, defAtThrow);
            placeFromSim();
            athletes.place(offIdx(r.slot), offPos[r.slot * 2]!, wz(offPos[r.slot * 2 + 1]!), 'catch');
            athletes.place(defIdx(r.defender), defPos[r.defender * 2]!, wz(defPos[r.defender * 2 + 1]!), 'reach');
            ball.set(plan.to.x, t.throw.catchHeightYards, wz(plan.to.y), true);
            rig.follow(plan.to.x, wz(plan.to.y));
          }
          if (ringOpen) {
            athletes.headPosition(offIdx(r.slot), v3);
            project(v3, scr);
            catchRing.set(scr.x, scr.y, 0);
            catchRing.setHot(true);
          }
          if (tapped || -remaining >= t.controls.catchLateGraceSeconds) arrive();
          break;
        }
        time += dt;
        live.sim.positionsAt(time, offPos, defPos);
        breakOnBall(live, plan, time, defPos, defAtThrow);
        placeFromSim();
        if (remaining < 0.4) {
          athletes.place(offIdx(r.slot), offPos[r.slot * 2]!, wz(offPos[r.slot * 2 + 1]!), 'catch');
          athletes.place(defIdx(r.defender), defPos[r.defender * 2]!, wz(defPos[r.defender * 2 + 1]!), 'reach');
        }
        const k = Math.min(1, phaseT / plan.flightSeconds);
        const bx = plan.from.x + (plan.to.x - plan.from.x) * k;
        const by = plan.from.y + (plan.to.y - plan.from.y) * k;
        const h = t.throw.releaseHeightYards + (t.throw.catchHeightYards - t.throw.releaseHeightYards) * k + 4 * arcHeight(plan) * k * (1 - k);
        ball.set(bx, h, wz(by), true);
        v3.set(bx, h, wz(by));
        trail.push(v3);
        rig.follow(bx, wz(by));
        if (!ringOpen && remaining <= t.controls.catchWindowSeconds) {
          ringOpen = true;
          catchRing.open(previewCatch(live, plan, 'good'), onCatchTap);
          if (guide.need('catch')) {
            guideSlow = true;
            guide.say('TAP AS THE RING CLOSES', 'Wait for TAP! A perfect tap makes the catch more likely.', 'top');
          }
        }
        if (ringOpen) {
          athletes.headPosition(offIdx(r.slot), v3);
          project(v3, scr);
          catchRing.set(scr.x, scr.y, remaining / t.controls.catchWindowSeconds);
          catchRing.setHot(remaining <= t.controls.catchGoodEarlySeconds);
        }
        break;
      }
      case 'run':
      case 'intReturn': {
        if (!world || !live) break;
        time += dt;
        if (phase === 'run' && runKind === 'pass') live.sim.positionsAt(time, offPos, scratchDef);
        const inp = phase === 'run' ? runCtl.read() : null;
        const move = phase === 'run' ? runCtl.takeMove() : null;
        const events = world.advance(dt, inp ? { stickX: inp.stickX, stickY: inp.stickY, sprint: inp.sprint, move } : NO_INPUT);
        onRunEvents(events);
        placeWorld(dt);
        holdBallAt(carrierIdx, 0);
        rig.follow(world.carrier.x, wz(world.carrier.y));
        runCtl.setStamina(world.stamina);
        if (phase === 'run' && !world.over) liveFeedback(world);
        if (world.over) {
          if (phase === 'intReturn') {
            intSpot = drive.ballOn + world.spotY;
            const six = world.log.some((e) => e.kind === 'touchdown');
            if (six) audio.incomplete();
            endPlay(finishPlay(live, { kind: 'pass', end: six ? 'pickSix' : 'interception', gain: 0, plan, pass, timing, events: [], carrier: null }));
          } else {
            const oc = outcomeFromRun(live, world, runKind, plan, pass, timing, drive.ballOn);
            endPlay(oc);
          }
        }
        break;
      }
      case 'dead': {
        // The ball stays with whoever went down with it.
        if (outcome && outcome.end !== 'incomplete' && carrierIdx >= 0) holdBallAt(carrierIdx, outcome.end === 'sack' ? 1 : 0);
        if (outcome?.end === 'incomplete' && plan) {
          const a = t.animation;
          ball.set(plan.to.x, Math.max(a.groundYards, t.throw.catchHeightYards - phaseT * a.incompleteDropYps * 2), wz(plan.to.y + phaseT * a.incompleteSkidYps));
        }
        const hold = outcome?.end === 'touchdown' || outcome?.end === 'pickSix' ? t.ui.endCardSeconds * 0.6 : outcome?.end === 'incomplete' ? t.ui.incompleteHoldSeconds : t.ui.tackleHoldSeconds;
        // The big screen clip plays out (or is tapped away) before the result.
        if (phaseT >= hold && !bigScreen.playing) showResult(clipPlayed);
        break;
      }
      case 'result': {
        if (nextQueued && phaseT >= t.ui.resultMinShowSeconds) {
          nextAfterResult(false);
          break;
        }
        // A finger on the banner keeps it up; after it lifts there is still a moment to finish reading.
        if (banner.isHeld) resultLeft = Math.max(resultLeft, t.ui.resultAfterHoldSeconds);
        else resultLeft -= dt;
        if (resultLeft <= 0) nextAfterResult(false);
        break;
      }
    }

    athletes.update(rawDt * o.timeScale * scale);
    bigScreen.update(rawDt);
    // The hand moved with this frame's pose: placed only before it, a carried ball trails a frame behind the
    // runner, out through his back.
    // (TypeScript cannot see that the phase code above sets it, hence the cast.)
    const h = held as { i: number; side: 0 | 1 } | null;
    if (h) holdBallAt(h.i, h.side);
    ball.update(rawDt);
    ring.update(rawDt);
    carrierRing.update(rawDt);
    confetti.update(rawDt);
    puff.update(rawDt);
    stage.stadium.update(rawDt);
    rig.update(rawDt);
    arc.update(rawDt);
    trail.update(stage.camera);
  }

  /** While he runs: the ring under him, yards gained above him, and FIRST DOWN! the moment he crosses the line. */
  function liveFeedback(w: RacWorld): void {
    const c = w.carrier;
    carrierRing.show(c.x, wz(c.y));
    const gain = Math.round(c.y);
    const toGoal = drive.ballOn + drive.toGo >= 100;
    const past = !toGoal && c.y >= drive.toGo;
    if (!firstDownShown && past) {
      firstDownShown = true;
      popAt(carrierIdx, 'FIRST DOWN!', 'good', true);
      audio.firstDown();
      stage.stadium.setExcitement(0.8);
      haptic(t.fx.hapticCatchMs);
    }
    v3.set(c.x, 0, wz(c.y));
    project(v3, scr);
    yards.set(scr.x, scr.y, gain, past);
  }

  function arrive(): void {
    if (!live || !plan) return;
    if (guideSlow) {
      guideSlow = false;
      guide.done('catch');
      guide.hide();
    }
    timing = tapped ?? 'good';
    pass = resolveCatch(live, plan, timing);
    catchRing.close();
    ring.hide();
    trail.reset();
    const r = live.sim.receivers[plan.receiverIndex]!;
    if (pass.outcome === 'complete') {
      audio.catchThud();
      haptic(t.fx.hapticCatchMs);
      slowT = t.fx.slowmoSeconds;
      stage.stadium.setExcitement(0.6);
      audio.crowd(0.6);
      popAt(offIdx(r.slot), timing === 'perfect' ? 'PERFECT CATCH!' : 'CAUGHT!', 'good', true);
      beginRun(startCatchRun(live, plan, timing, time, offPos, defPos), 'pass', offIdx(r.slot));
      return;
    }
    if (pass.outcome === 'interception') {
      audio.incomplete();
      rig.addShake(t.fx.shakeBroke);
      popAt(defIdx(r.defender), 'PICKED OFF!', 'bad', true);
      const w = startRun(live, 'defense', r.defender, plan.to, { x: 0, y: -4 }, offPos, defPos, { aim: null, auto: true, blocking: false });
      world = w;
      carrierIdx = defIdx(r.defender);
      runCtl.show(false);
      rig.setMode('chase', 1);
      setPhase('intReturn');
      return;
    }
    audio.incomplete();
    popAt(offIdx(r.slot), pass.dropped ? 'DROPPED' : pass.sailRoll !== null ? 'OVERTHROWN' : 'INCOMPLETE', 'bad', true);
    athletes.place(offIdx(r.slot), offPos[r.slot * 2]!, wz(offPos[r.slot * 2 + 1]!), 'idle');
    world = null;
    endPlay(finishPlay(live, { kind: 'pass', end: 'incomplete', gain: 0, plan, pass, timing, events: [], carrier: null }));
  }

  // Start on the title (the field is live behind it).
  showFormation();
  rig.setMode('call');
  rig.snapTo(0, wz(-5));
  hud.update(drive, match);
  playCall.hide();
  if (o.skipTitle) {
    title.hide();
    startMatch();
  }
  stage.start(frame);

  return {
    stats: stage.stats,
    debug,
    athletes: athletes instanceof SkinnedAthletesView ? 'rigged' : 'primitive',
    dispose: () => {
      runCtl.dispose();
      athletes.dispose();
      stage.dispose();
    },
  };
}

/** The rigged athletes when the model loaded and fits, else the primitive ones, so the game always starts. */
function createAthletes(squads: SquadSpec[], t: Tunables, model: AthleteModel | null, logos: KitLogos): AthleteRig {
  if (model) {
    try {
      return new SkinnedAthletesView(squads, t, model, logos);
    } catch (err) {
      console.warn('Rigged athletes failed, using primitives:', err);
    }
  }
  return new AthletesView(squads, t);
}
